import React, { useEffect, useState } from "react";
import {
  Box,
  Badge,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  SimpleGrid,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import FinancePageShell from "./common/FinancePageShell";
import FinanceSectionCard from "./common/FinanceSectionCard";
import FinanceStatCard from "./common/FinanceStatCard";
import { financeApi } from "./financeApi";
import { formatDateTime, formatMoney } from "./common/formatters";

const AdminSellerFinancePage: React.FC = () => {
  const { sellerUserId = "" } = useParams<{ sellerUserId: string; }>();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);

  useEffect(() => {
    const run = async () => {
      if (!sellerUserId) {
        setPayload(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const res = await financeApi.getSellerFinance(sellerUserId);
        setPayload(res?.data || null);
      } catch (error: any) {
        console.error("Failed to load admin seller finance:", error);

        toast({
          status: "error",
          title: "Failed to load seller finance",
          description:
            error?.response?.data?.message ||
            error?.message ||
            "Please try again.",
          duration: 4000,
          isClosable: true,
        });

        setPayload(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [sellerUserId, toast]);

  if (loading) {
    return (
      <FinancePageShell title="Seller Finance">
        <VStack py={10}>
          <Spinner />
          <Text>Loading seller finance...</Text>
        </VStack>
      </FinancePageShell>
    );
  }

  if (!payload) {
    return (
      <FinancePageShell
        title="Seller Finance"
        subtitle={`Viewing finance details for seller ${sellerUserId}`}
      >
        <FinanceSectionCard title="No seller finance found">
          <Text>
            No finance data was found for this seller yet. Check that the seller
            ID is correct and that sales allocations have been created.
          </Text>
        </FinanceSectionCard>
      </FinancePageShell>
    );
  }

  const balance = payload?.balance || {};

  return (
    <FinancePageShell
      title="Seller Finance"
      subtitle={`Viewing finance details for seller ${sellerUserId}`}
    >
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
        <FinanceStatCard
          label="Gross Sales"
          value={formatMoney(balance.grossSales)}
        />
        <FinanceStatCard
          label="Platform Fees"
          value={formatMoney(balance.platformFees)}
        />
        <FinanceStatCard
          label="Gateway Fees"
          value={formatMoney(balance.gatewayFees)}
        />
        <FinanceStatCard
          label="Refunded"
          value={formatMoney(balance.refundedAmount)}
        />
        <FinanceStatCard
          label="Pending"
          value={formatMoney(balance.pendingPayable)}
        />
        <FinanceStatCard
          label="Eligible"
          value={formatMoney(balance.eligiblePayable)}
        />
        <FinanceStatCard
          label="Paid Out"
          value={formatMoney(balance.paidOutAmount)}
        />
      </SimpleGrid>

      <FinanceSectionCard title="Allocations">
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Product</Th>
                <Th>Status</Th>
                <Th>Payment</Th>
                <Th isNumeric>Gross</Th>
                <Th isNumeric>Net</Th>
                <Th>Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(payload?.allocations || []).map((row: any) => (
                <Tr key={row._id}>
                  <Td>{row.productTitle || row.productId || "Item"}</Td>
                  <Td>
                    <Badge>{row.settlementStatus || "pending"}</Badge>
                  </Td>
                  <Td>{row.paymentStatus || "pending"}</Td>
                  <Td isNumeric>
                    {formatMoney(row.grossAmount, row.currency)}
                  </Td>
                  <Td isNumeric>
                    {formatMoney(row.netSellerAmount, row.currency)}
                  </Td>
                  <Td>{formatDateTime(row.createdAt)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {!(payload?.allocations || []).length ? (
            <Text mt={3}>No seller allocations found.</Text>
          ) : null}
        </Box>
      </FinanceSectionCard>

      <FinanceSectionCard title="Payout history">
        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Payout item</Th>
                <Th>Status</Th>
                <Th isNumeric>Amount</Th>
                <Th>Reference</Th>
                <Th>Date</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(payload?.payouts || []).map((row: any) => (
                <Tr key={row._id}>
                  <Td>{row._id}</Td>
                  <Td>
                    <Badge>{row.status || "pending"}</Badge>
                  </Td>
                  <Td isNumeric>{formatMoney(row.amount, row.currency)}</Td>
                  <Td>{row.transferReference || "—"}</Td>
                  <Td>{formatDateTime(row.paidAt || row.createdAt)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {!(payload?.payouts || []).length ? (
            <Text mt={3}>No payout items found.</Text>
          ) : null}
        </Box>
      </FinanceSectionCard>
    </FinancePageShell>
  );
};

export default AdminSellerFinancePage;